import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { apiBlob } from "../../../../services/api/apiClient";

type PreviewDoc = {
  id: string;
  displayName: string;
  extension?: string;
};

// The invoice row keeps the client on the invoice screen while showing the
// draw or invoice document in the same pop-up the document library uses.
export default function InvoiceDocPreview({
  doc,
  onClose,
}: {
  doc: PreviewDoc | null;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!doc) {
      setUrl(null);
      setError("");
      return;
    }
    const controller = new AbortController();
    let objectURL = "";
    setUrl(null);
    setError("");

    apiBlob({
      endpoint: "/document-library/item/content",
      params: { id: doc.id },
      withAuth: true,
      signal: controller.signal,
    })
      .then((blob) => {
        objectURL = URL.createObjectURL(blob);
        setUrl(objectURL);
      })
      .catch((reason: any) => {
        if (reason?.name !== "AbortError") {
          setError("Could not open this document. Try again in a moment.");
        }
      });

    return () => {
      controller.abort();
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [doc]);

  if (!doc) return null;
  const isPdf = (doc.extension || "").toLowerCase() === "pdf";

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 0, height: "85vh" } }}
    >
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
        {doc.displayName}
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--surface-container-low, #f7f7f7)",
          overflow: "hidden",
        }}
      >
        {error ? (
          <Typography sx={{ p: 3 }}>{error}</Typography>
        ) : !url ? (
          <CircularProgress size={28} />
        ) : isPdf ? (
          <iframe
            title={doc.displayName}
            src={url}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        ) : (
          <img
            src={url}
            alt={doc.displayName}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
